import { usersId, type UsersId } from "./Users";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for accounts */
export type AccountsId = string & { __flavor: "AccountsId" };

/**
 * Represents the table public.accounts
 * @uniqueConstraints {"accounts_pkey":{"name":"accounts_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"accounts_user_id_index":{"name":"accounts_user_id_index","isPrimary":false,"isUnique":false,"columns":[{"name":"user_id","definition":"user_id","predicate":null}]}}
 */
export default interface AccountsTable {
  id: ColumnType<AccountsId, AccountsId, AccountsId>;

  accountId: ColumnType<string, string, string>;

  providerId: ColumnType<string, string, string>;

  userId: ColumnType<UsersId, UsersId, UsersId>;

  accessToken: ColumnType<string | null, string | null, string | null>;

  refreshToken: ColumnType<string | null, string | null, string | null>;

  idToken: ColumnType<string | null, string | null, string | null>;

  accessTokenExpiresAt: ColumnType<Date | null, Date | string | null, Date | string | null>;

  refreshTokenExpiresAt: ColumnType<Date | null, Date | string | null, Date | string | null>;

  scope: ColumnType<string | null, string | null, string | null>;

  password: ColumnType<string | null, string | null, string | null>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  updatedAt: ColumnType<Date, Date | string, Date | string>;
}

export type Accounts = Selectable<AccountsTable>;

export type NewAccounts = Insertable<AccountsTable>;

export type AccountsUpdate = Updateable<AccountsTable>;

export const accountsId = z.string() as unknown as z.Schema<AccountsId>;

export const accounts = z.object({
  id: accountsId,
  accountId: z.string(),
  providerId: z.string(),
  userId: usersId,
  accessToken: z.string().nullable(),
  refreshToken: z.string().nullable(),
  idToken: z.string().nullable(),
  accessTokenExpiresAt: z.date().nullable(),
  refreshTokenExpiresAt: z.date().nullable(),
  scope: z.string().nullable(),
  password: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const accountsInitializer = z.object({
  id: accountsId,
  accountId: z.string(),
  providerId: z.string(),
  userId: usersId,
  accessToken: z.string().optional().nullable(),
  refreshToken: z.string().optional().nullable(),
  idToken: z.string().optional().nullable(),
  accessTokenExpiresAt: z.date().optional().nullable(),
  refreshTokenExpiresAt: z.date().optional().nullable(),
  scope: z.string().optional().nullable(),
  password: z.string().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date(),
});

export const accountsMutator = z.object({
  id: accountsId.optional(),
  accountId: z.string().optional(),
  providerId: z.string().optional(),
  userId: usersId.optional(),
  accessToken: z.string().optional().nullable(),
  refreshToken: z.string().optional().nullable(),
  idToken: z.string().optional().nullable(),
  accessTokenExpiresAt: z.date().optional().nullable(),
  refreshTokenExpiresAt: z.date().optional().nullable(),
  scope: z.string().optional().nullable(),
  password: z.string().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

// ── constraints for Accounts ─────────────────────────────────
export const AccountsConstraints = {
  accounts_pkey: {
    name: "accounts_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  accounts_user_id_index: {
    name: "accounts_user_id_index",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "user_id", definition: "user_id", predicate: null }],
  },
} as const;
export type AccountsConstraintsType = typeof AccountsConstraints;
